import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Save, Plus } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { purchaseService } from '../../services/purchaseService';
import api from '../../services/api';
import MantleGrid from '../../components/MantleGrid';
import Field from '../../components/Field';
import { cell, GRID } from '../../styles/gridStyles';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';

// =============================================================================
// Non-Stocked Pricing — edits bud_opex_items (version + site + item_label +
// period -> cost_amount). One row per (site, item), the 12 months shown as
// editable columns. Version + site come from the top bar; there is no year (the
// version implies it).
//
// "All sites" in the top bar shows every site's items and reveals a Site column
// (each row keeps its own site); picking a site hides it again.
// =============================================================================

// Row identity. With "All sites" the same item label can exist per site, so the
// site is part of the key for row ids and dirty tracking.
const rowKeyOf = (it) => `${it.site_code || ''}::${it.item_label}`;

const NonStockedPricing = () => {
  const { selectedSite, selectedVersion, fyStartMonth } = useAppContext();

  const [items, setItems] = useState([]);   // [{ site_code, item_label, periods: { p: cost } }]
  const [dirty, setDirty] = useState({});   // { `${rowKey}:${period}`: true }
  const [sites, setSites] = useState([]);   // [{ code, name }] — for the Site column
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newSite, setNewSite] = useState('');

  // Version is required; site is not — no site means "All sites".
  const ready = !!selectedVersion;
  const allSites = ready && !selectedSite;

  const costOf = useCallback((it, p) => Number(it.periods?.[p] || 0), []);
  const rowTotal = useCallback((it) => PERIODS.reduce((a, p) => a + costOf(it, p), 0), [costOf]);
  const siteName = useCallback(
    (code) => sites.find((s) => s.code === code)?.name || code || '—',
    [sites],
  );

  // ---- Load grid data ------------------------------------------------------
  const loadItems = useCallback(async () => {
    if (!selectedVersion) { setItems([]); return; }
    setIsLoading(true);
    try {
      const r = await purchaseService.getNonStocked();
      setItems(r.success ? r.data.items : []);
      setDirty({});
    } catch (err) {
      console.error('Failed to load non-stocked pricing:', err.response?.data || err.message);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
    // selectedSite isn't read here — the API takes it from the x-site-code
    // header — but switching site must still refetch, so it stays a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVersion, selectedSite]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // Site names for the Site column / add-item picker.
  useEffect(() => {
    let cancelled = false;
    if (!ready) return undefined;
    api.get('/sites/load-all')
      .then((r) => { if (!cancelled) setSites(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ready]);

  // ---- Grid: Item + 12 months + Total --------------------------------------
  const columns = useMemo(() => ([
    // Site only appears in "All sites" mode — otherwise every row would repeat
    // the site already chosen in the top bar.
    ...(allSites ? [{ columnId: 'site', width: 150, resizable: true }] : []),
    { columnId: 'item', width: 260, resizable: true },
    { columnId: 'status', width: 110, resizable: true },
    { columnId: 'uom', width: 70, resizable: true },
    ...PERIODS.map((p) => ({ columnId: `P${p}`, width: 84, resizable: true })),
    { columnId: 'total', width: 110, resizable: true },
  ]), [allSites]);

  const gridRows = useMemo(() => {
    const headerRow = {
      rowId: 'header',
      height: GRID.size.headerRow,
      cells: [
        ...(allSites ? [cell.header('Site')] : []),
        cell.header('Non-Stocked Item'),
        cell.header('Status'),
        cell.header('UoM'),
        ...PERIODS.map((p) => cell.header(periodLabel(p, fyStartMonth), { align: 'right' })),
        cell.header('Total', { align: 'right' }),
      ],
    };

    const dataRows = items.map((it, idx) => ({
      rowId: rowKeyOf(it),
      height: GRID.size.dataRow,
      cells: [
        ...(allSites ? [cell.text(siteName(it.site_code), { idx })] : []),
        cell.label(it.item_label, { idx }),
        // Awaiting = this item still has P×Q lines procurement hasn't priced.
        it.awaiting
          ? cell.tag('❗ Awaiting', {
              active: true, activeColor: '#b45309', activeBg: '#fef3c7',
            })
          : cell.text('', { idx }),
        cell.editText(it.unit_of_measure, { idx }),
        ...PERIODS.map((p) => cell.number(costOf(it, p), { editable: true, idx })),
        cell.aggregate(rowTotal(it), { kind: 'year' }),
      ],
    }));

    const totalRow = {
      rowId: 'totals',
      height: GRID.size.totalRow,
      cells: [
        ...(allSites ? [cell.totalBlank()] : []),
        cell.totalLabel('TOTAL'),
        cell.totalBlank(),
        cell.totalBlank(),
        ...PERIODS.map((p) => cell.totalNumber(items.reduce((a, it) => a + costOf(it, p), 0))),
        cell.totalNumber(items.reduce((a, it) => a + rowTotal(it), 0)),
      ],
    };

    return [headerRow, ...dataRows, totalRow];
  }, [items, fyStartMonth, costOf, rowTotal, allSites, siteName]);

  // Which dirty keys a batch of changes touches. Derived from the change list
  // alone so the dirty map can be updated with its own functional setState.
  const dirtyKeysFor = useCallback((changes) => {
    const keys = [];
    for (const ch of changes) {
      const rowId = String(ch.rowId);
      if (rowId === 'header' || rowId === 'totals') continue;
      const col = String(ch.columnId);

      // UoM is item-level: re-save it across all the item's months.
      if (col === 'uom') {
        for (const p of PERIODS) keys.push(`${rowId}:${p}`);
        continue;
      }

      if (!col.startsWith('P')) continue;
      const period = Number(col.slice(1));
      if (!(period >= 1 && period <= 12)) continue;
      if (isNaN(Number(ch.newCell?.value))) continue;
      keys.push(`${rowId}:${period}`);
    }
    return keys;
  }, []);

  // A multi-cell paste arrives as one onCellsChanged call PER CELL in a single
  // tick, so both updates must use the functional form. Building the dirty map
  // from the `dirty` closure instead would restart from the same stale base on
  // every call and only the last pasted cell would survive to be saved.
  const handleChanges = useCallback((changes) => {
    setItems((prev) => {
      const next = prev.map((it) => ({ ...it, periods: { ...it.periods } }));
      for (const ch of changes) {
        const col = String(ch.columnId);
        if (ch.rowId === 'header' || ch.rowId === 'totals') continue;
        const item = next.find((i) => rowKeyOf(i) === String(ch.rowId));
        if (!item) continue;

        if (col === 'uom') {
          item.unit_of_measure = (ch.newCell?.text ?? '').trim() || null;
          continue;
        }

        if (!col.startsWith('P')) continue;
        const period = Number(col.slice(1));
        if (!(period >= 1 && period <= 12)) continue;
        const val = Number(ch.newCell?.value);
        if (isNaN(val)) continue;
        item.periods[period] = val;
      }
      return next;
    });

    setDirty((prev) => {
      const d = { ...prev };
      for (const k of dirtyKeysFor(changes)) d[k] = true;
      return d;
    });
  }, [dirtyKeysFor]);

  // ---- Add a new (free-form) item label ------------------------------------
  // In "All sites" mode the new item needs a site of its own; otherwise it
  // belongs to the site selected in the top bar.
  const addSite = allSites ? newSite : selectedSite;

  const addItem = () => {
    const label = newLabel.trim();
    if (!label || !addSite) return;
    if (items.some((i) => i.site_code === addSite && i.item_label.toLowerCase() === label.toLowerCase())) {
      setNewLabel('');
      return; // already present for this site
    }
    const fresh = { site_code: addSite, item_label: label, unit_of_measure: null, periods: {} };
    setItems((prev) => [...prev, fresh].sort((a, b) =>
      (a.site_code || '').localeCompare(b.site_code || '') || a.item_label.localeCompare(b.item_label)));
    // Mark all 12 months dirty so a brand-new item persists (seeds 0s).
    setDirty((prev) => {
      const d = { ...prev };
      const key = rowKeyOf(fresh);
      for (const p of PERIODS) d[`${key}:${p}`] = true;
      return d;
    });
    setNewLabel('');
  };

  // ---- Save ----------------------------------------------------------------
  const handleSave = async () => {
    const changes = [];
    for (const it of items) {
      const key = rowKeyOf(it);
      for (const p of PERIODS) {
        if (dirty[`${key}:${p}`]) {
          changes.push({
            // site_code travels per row so "All sites" can save every site in
            // one request (the API falls back to the header site otherwise).
            site_code: it.site_code || selectedSite || null,
            item_label: it.item_label,
            financial_period: p,
            cost_amount: costOf(it, p),
            unit_of_measure: it.unit_of_measure || null,
          });
        }
      }
    }
    if (changes.length === 0) return;
    setIsSaving(true);
    try {
      await purchaseService.saveNonStocked({ changes });
      await loadItems();
    } catch (err) {
      console.error('Failed to save non-stocked pricing:', err.response?.data || err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = Object.keys(dirty).length > 0;
  const awaitingCount = items.filter((i) => i.awaiting).length;

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      {/* Header */}
      <div className="shrink-0 px-6 pt-6 pb-4">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Non-Stocked Pricing</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Capture the cost of non-stocked (opex) items per month. Version and site come from the top bar;
          choose "All sites" to see every site at once, with a Site column. Items flagged
          <span className="font-semibold text-amber-600"> ❗ Awaiting</span> still have P×Q lines with no
          procurement price — entering a cost here clears them.
        </p>
        {awaitingCount > 0 && (
          <p className="mt-2 inline-block px-2.5 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium">
            {awaitingCount} item{awaitingCount === 1 ? '' : 's'} awaiting procurement
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-end gap-3">
          {allSites && (
            <Field label="Site">
              <select
                value={newSite}
                onChange={(e) => setNewSite(e.target.value)}
                disabled={!ready}
                className="text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50 w-44"
              >
                <option value="">Select site…</option>
                {sites.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Add item">
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }}
                placeholder="New item label…"
                disabled={!ready}
                className="text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50 w-52"
              />
              <button
                type="button"
                onClick={addItem}
                title={allSites && !newSite ? 'Pick a site first' : 'Add item'}
                disabled={!newLabel.trim() || !ready || !addSite}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add item
              </button>
            </div>
          </Field>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 min-h-0 px-6 pb-6">
        <div className="h-full rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {!ready ? (
            <div className="p-10 text-center text-sm text-slate-400">
              Select a budget version in the top bar to begin.
            </div>
          ) : (
            <MantleGrid
              rows={gridRows}
              columns={columns}
              isLoading={isLoading}
              onCellsChanged={handleChanges}
              stickyBottomRows={0}
              hint="Enter each month’s cost. Add items with the field above, then Save."
              toolbar={
                <button
                  onClick={handleSave}
                  disabled={isSaving || !hasChanges}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white disabled:opacity-50 shadow-sm transition-colors ${
                    hasChanges
                      ? 'bg-emerald-600 hover:bg-emerald-700'
                      : 'bg-slate-400 dark:bg-slate-600 hover:bg-slate-500 dark:hover:bg-slate-500'
                  }`}
                >
                  <Save className="w-3.5 h-3.5" />
                  {isSaving ? 'Saving…' : 'Save'}
                </button>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default NonStockedPricing;
